import { useMutation } from "@apollo/client";
import { ChangeEvent, InputHTMLAttributes, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import styles from "@components/fields/DirectUploadFileInput.module.css";
import Nbsp from "@components/Nbsp";
import Icon from "@components/text/Icon";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import Heading2 from "@components/typography/Heading2";
import useCreateFileResource from "@hooks/mutations/useCreateFileResource";
import useDragAndDropFile from "@hooks/useDragAndDropFile";
import {
  DirectUploadCreateDocument,
  DirectUploadCreateMutation,
  DirectUploadCreateMutationVariables,
  FileResourceFragment
} from "@typing/Generated";
import { computeChecksumMd5, directUpload } from "@utils/fileUtils";

type DirectUpload = NonNullable<NonNullable<DirectUploadCreateMutation["directUploadCreate"]>["directUpload"]>;

type DirectUploadAndFile = {
  directUpload: DirectUpload;
  file: File;
};

type UploadProps = {
  onUploadComplete: (uploadAndFile: DirectUploadAndFile) => void;
  uploadAndFile: DirectUploadAndFile;
};

const Upload = ({ onUploadComplete, uploadAndFile }: UploadProps) => {
  useEffect(() => {
    directUpload({
      file: uploadAndFile.file,
      jsonEncodedHeaders: uploadAndFile.directUpload.headers,
      onProgress: () => {},
      url: uploadAndFile.directUpload.url
    }).then(() => {
      onUploadComplete(uploadAndFile);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div />;
};

type Props = InputHTMLAttributes<HTMLInputElement> & {
  clearFileTypeError?: () => void;
  folderId?: string;
  journeyId: string;
  onSuccess?: (fileResource: FileResourceFragment) => void;
  overrideContent?: ReactNode;
  validTypes?: string[];
};

const DirectUploadFileResourceInput = ({
  accept,
  clearFileTypeError,
  folderId,
  journeyId,
  onSuccess,
  overrideContent,
  validTypes,
  ...inputProps
}: Props) => {
  const intl = useIntl();
  const [directUploadAndFiles, setDirectUploadAndFiles] = useState<DirectUploadAndFile[]>([]);
  const busyFiles = useRef<File[]>([]);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string>();

  const inputRef = useRef<HTMLInputElement>(null);
  const [createDirectUpload] = useMutation<DirectUploadCreateMutation, DirectUploadCreateMutationVariables>(
    DirectUploadCreateDocument
  );

  const createFileResource = useCreateFileResource(journeyId);

  const handleUploadComplete = useCallback(
    (uploadAndFile: DirectUploadAndFile) => {
      const index = busyFiles.current.indexOf(uploadAndFile.file);
      if (index >= 0) {
        const newBusyFiles = [...busyFiles.current];
        newBusyFiles.splice(index, 1);
        busyFiles.current = newBusyFiles;
        if (newBusyFiles.length === 0) {
          setBusy(false);

          createFileResource({
            variables: {
              blobId: uploadAndFile.directUpload.signedBlobId,
              folderId,
              journeyId,
              title: uploadAndFile.file.name
            }
          }).then(res => {
            if (res?.data?.fileResourceCreate?.fileResource) {
              onSuccess?.(res.data?.fileResourceCreate?.fileResource);
            }
          });
        }
      }
    },
    [createFileResource, folderId, journeyId, onSuccess]
  );

  const handleStartUpload = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  }, [inputRef]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e?.target?.files;

    if (files) {
      setDirectUploadAndFiles([]);

      const createDirectUploadPromises: Promise<DirectUploadAndFile>[] = [];
      setBusy(true);
      const newBusyFiles: File[] = [...busyFiles.current];

      for (let index = 0; index < files.length ?? 0; ++index) {
        const file = e?.target?.files?.[index] as File;
        if (validTypes && !validTypes.includes(file.type)) {
          setError(intl.formatMessage({ id: "errors.fileResource.blobId.invalidType" }));
          setBusy(false);
        } else {
          if (clearFileTypeError) {
            clearFileTypeError();
          }
          newBusyFiles.push(file);
          createDirectUploadPromises.push(
            new Promise((resolve, reject) => {
              computeChecksumMd5(file).then(checksum => {
                createDirectUpload({
                  variables: {
                    byteSize: file.size,
                    checksum,
                    contentType: file.type,
                    filename: file.name
                  }
                }).then(response => {
                  const directUpload = response.data?.directUploadCreate?.directUpload;
                  if (directUpload) {
                    resolve({ directUpload, file });
                  } else {
                    reject("NO BLOB ID");
                  }
                });
              });
            })
          );
          busyFiles.current = newBusyFiles;

          Promise.all<DirectUploadAndFile>(createDirectUploadPromises).then(result => {
            const existing: DirectUploadAndFile[] = [];
            const newDirectUploadAndFiles = [...existing, ...result];

            setDirectUploadAndFiles(newDirectUploadAndFiles);
          });
        }
      }
    }
  };

  const { isDraggedOver, ...dragAndDropProps } = useDragAndDropFile({
    onDrop: files => handleChange({ target: { files } } as unknown as ChangeEvent<HTMLInputElement>)
  });

  return (
    <>
      <div>
        <input
          accept={accept ? accept : "*"}
          aria-label={intl.formatMessage({
            id: "accessibility.ariaLabels.attachFiles"
          })}
          multiple={false}
          name="file-resource-input"
          onChange={handleChange}
          ref={inputRef}
          tabIndex={-1}
          type="file"
          {...inputProps}
          className={styles.input}
        />
        <div {...dragAndDropProps}>
          {overrideContent ? (
            <div onClick={handleStartUpload}>{overrideContent}</div>
          ) : (
            <ButtonLink
              className={`${styles.fileResourceContainer} ${isDraggedOver ? styles.dragOver : ""}`}
              disabled={busy}
              onClick={handleStartUpload}
              stylized={false}
            >
              {busy ? (
                <Icon color="gray" icon="spinner" size="xlarge" />
              ) : (
                <div>
                  <Heading2 bold color="gray">
                    <FormattedMessage id="components.CarePlan.sections.drive.uploadFiles" />
                  </Heading2>
                  <div className="margin-10-top">
                    <BodyCopy bold color="gray">
                      <FormattedMessage id="components.CarePlan.sections.drive.dragAndDrop" />
                    </BodyCopy>
                    <Nbsp />
                    <BodyCopy color="primary-500">
                      <FormattedMessage id="components.CarePlan.sections.drive.browse" />
                    </BodyCopy>
                  </div>
                </div>
              )}
            </ButtonLink>
          )}
        </div>

        {directUploadAndFiles?.map(directUploadAndFile => (
          <Upload
            key={directUploadAndFile.directUpload.signedBlobId}
            onUploadComplete={handleUploadComplete}
            uploadAndFile={directUploadAndFile}
          />
        ))}
      </div>
      {error && (
        <div className={styles.errorWrapper}>
          <BodyCopy bold color="danger">
            {error}
          </BodyCopy>
        </div>
      )}
    </>
  );
};

export default DirectUploadFileResourceInput;
